<script lang="ts">
import { capitalCase } from 'change-case'
import { defineComponent } from 'vue'

/**
 * A genertic container for a modal.
 *
 * Usage notes:
 * - The modal title must have the ID `modal-{id}`
 * - Upon displaying the modal, the first focusable must receive the focus
 * - If the primary action of the modal is destructive, set the initial focus to the least destructive action
 * - Keyboard interactions must be available (tab, shift+tab and esc)
 *
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */
export default defineComponent({
  inheritAttrs: false,
  props: {
    /**
     * An ID for the modal, must be unique.
     */
    id: {
      type: String,
      required: true,
    },
    /**
     * Adds classes to the button which opens the modal.
     */
    buttonClass: {
      type: String,
    },
    /**
     * Adds a title to the button which opens the modal.
     */
    buttonTitle: {
      type: String,
    },
    /**
     * Open status of the modal.
     */
    open: {
      type: Boolean,
    },
    /**
     * Adds classes to the close button which closes the modal.
     */
    closeButtonClass: {
      type: String,
      default: 'right-4 top-4 z-10',
    },
    /**
     * Closes the modal when a user clicks outside the contents.
     */
    closeOnBlur: {
      type: Boolean,
      default: true,
    },
    /**
     * If false, the modal will be displayed as a div
     */
    dialogMode: {
      type: Boolean,
      default: true,
    },
    /**
     * Allow dismissing the modal
     */
    allowDismissal: {
      type: Boolean,
      default: true,
    },
    /**
     * Disable closing the modal (handle in parent)
     */
    disableClose: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close'],
  setup() {
    return {
      capitalCase,
    }
  },
  data() {
    return {
      isOpen: false,
    }
  },
  computed: {
    modal(): HTMLDialogElement | HTMLDivElement | null {
      return this.dialogMode
        ? ((this.$refs.dialogModal as unknown as HTMLDialogElement) ?? null)
        : ((this.$refs.divModal as unknown as HTMLDivElement) ?? null)
    },
  },
  watch: {
    open: {
      handler(isOpen: boolean) {
        if (isOpen) {
          this.openModal()
          // Only close if modal was actually open
        } else if (this.isOpen) {
          this.closeModal(true)
        }
      },
      immediate: true,
    },
  },
  beforeMount() {
    document.addEventListener('keydown', this.closeOnEsc.bind(this))
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.closeOnEsc.bind(this))
  },
  methods: {
    openModal() {
      // Timeout allows modal to be opened on first page load
      setTimeout(() => {
        this.$emit('open')
        this.isOpen = true
        if (this.dialogMode) {
          ;(this.modal as HTMLDialogElement)?.showModal()
        }
      }, 250)
    },
    closeModal(forceDismissal: boolean = false) {
      if (!this.allowDismissal && !forceDismissal) {
        return
      }

      this.$emit('close')

      if (this.disableClose) {
        return
      }

      this.isOpen = false

      if (this.dialogMode) {
        ;(this.modal as HTMLDialogElement)?.close()
      }
    },
    closeOnEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.closeModal(true)
      }
    },
    closeWhenOffTarget(event: EventWithTarget<MouseEvent>) {
      const clickedOnToast = Array.from(event.target.classList).some(
        (className: string) => className.startsWith('Vue-Toastification')
      )

      if (!this.allowDismissal || clickedOnToast) {
        return
      }

      if (this.closeOnBlur) {
        this.closeModal()
      }
    },
  },
})
</script>

<template>
  <slot name="button" :is-open :open-modal />

  <teleport to="#modal-teleport">
    <dialog
      v-if="dialogMode"
      v-show="isOpen"
      :id
      ref="dialogModal"
      role="alertdialog"
      aria-modal="true"
      :aria-label="capitalCase(id)"
      class="modal m-auto rounded-lg h-fit"
      :class="isOpen ? 'flex is-open' : ''"
      v-bind="$attrs"
      @mousedown.self="
        (e) => closeWhenOffTarget(e as EventWithTarget<MouseEvent>)
      "
    >
      <a
        v-if="allowDismissal"
        class="absolute flex cursor-pointer items-center justify-center rounded-full p-1"
        :class="closeButtonClass"
        @click.passive="closeModal(true)"
      >
        <AppIcon :icon="Icons.Times" size="sm" />
      </a>

      <slot :is-open :close-modal :open-modal />
    </dialog>

    <div
      v-if="!dialogMode && isOpen"
      :id
      ref="divModal"
      :aria-label="capitalCase(id)"
      class="fixed top-0 left-0 z-[9999999990] mb-4 flex w-full items-center justify-center bg-black/80 p-4 h-full"
      :class="isOpen ? 'flex is-open' : ''"
      @mousedown.self="
        (e) => closeWhenOffTarget(e as EventWithTarget<MouseEvent>)
      "
    >
      <div class="inset-0 max-h-full overflow-y-auto rounded-lg">
        <div
          v-bind="$attrs"
          class="relative overflow-y-auto bg-pure-white rounded-lg"
        >
          <a
            v-if="allowDismissal"
            class="absolute flex cursor-pointer items-center justify-center rounded-full p-1"
            :class="closeButtonClass"
            @click.passive="closeModal(true)"
          >
            <AppIcon :icon="Icons.Times" size="sm" />
          </a>

          <slot :is-open :close-modal :open-modal />
        </div>
      </div>
    </div>
  </teleport>
</template>
